import chroma from "chroma-js";

// Pulled from https://colorbrewer2.org/#type=qualitative&scheme=Set1&n=9 with some modifications
const brewerSet1 = [
  "#377eb8",
  "#4daf4a",
  "#984ea3",
  "#ff7f00",
  "#E1E12D",
  "#a65628",
  "#f781bf",
  "#999999",
  "#e41a1c",
];

/**
 * Hands out series colors per panel. Each panel draws the palette in order,
 * then keeps going with darker and lighter shades of it, so a panel holding
 * more series than the palette has colors (for example one signal overlaid
 * from many recording sessions) gets a different color per series until the
 * shades bottom out at black or top out at white, which with a nine-color
 * palette takes more than fifty series. The sequence depends only on the
 * palette and the number of draws, so two instances given the same palette
 * hand out the same colors.
 */
export default class CyclingColorScale {
  private colorByKeyByPanel = new Map<string, Map<string, string>>();
  private colors: string[];
  private drawCountByPanel = new Map<string, number>();

  constructor(colors: string[] = brewerSet1) {
    if (colors.length < 1) {
      throw new Error(
        "CyclingColorScale requires a non-empty list of colors to initialize",
      );
    }
    this.colors = colors;
  }

  /**
   * The color held for `key` within `panelId`, drawing the panel's next color
   * on first sight of the key and keeping it for later calls. Asking again
   * with the same key returns the same color and consumes no palette slot.
   * `reset` and `resetForPanel` drop these assignments along with the panel's
   * position in the palette, so a removed panel leaves nothing behind and a
   * reset panel starts again from the first palette color.
   */
  public colorFor(panelId: string, key: string): string {
    let colorByKey = this.colorByKeyByPanel.get(panelId);
    if (colorByKey === undefined) {
      colorByKey = new Map<string, string>();
      this.colorByKeyByPanel.set(panelId, colorByKey);
    }
    const existing = colorByKey.get(key);
    if (existing !== undefined) {
      return existing;
    }
    const color = this.getNextColorForPanel(panelId);
    colorByKey.set(key, color);
    return color;
  }

  /**
   * Advances `panelId`'s position and returns the color it lands on: the
   * palette in order, then a shade of the palette color once the palette has
   * been used up. Every call takes a slot, so callers that want one stable
   * color per series should go through `colorFor` instead.
   */
  public getNextColorForPanel(panelId: string): string {
    const drawCount = this.drawCountByPanel.get(panelId) ?? 0;
    this.drawCountByPanel.set(panelId, drawCount + 1);
    const base = this.colors[drawCount % this.colors.length];
    const pass = Math.floor(drawCount / this.colors.length);
    if (pass === 0) {
      return base;
    }
    // Passes alternate darkening and lightening and share a step amount, so
    // each pair of passes lands one step further from the palette color.
    const step = Math.ceil(pass / 2);
    const shaded =
      pass % 2 === 1 ? chroma(base).darken(step) : chroma(base).brighten(step);
    return shaded.hex();
  }

  public reset() {
    this.drawCountByPanel.clear();
    this.colorByKeyByPanel.clear();
  }

  public resetForPanel(panelId: string) {
    this.drawCountByPanel.delete(panelId);
    this.colorByKeyByPanel.delete(panelId);
  }
}

export const cyclingColorScale = new CyclingColorScale();
